import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  NetworkUtilsService,
  URL_DICT,
} from '../../../service/network-utils.service';

import {
  AdminTherapistBookingRequest,
  SearchUserResponse,
  SlotAvailability,
} from 'projects/app-core/src/app/session/components/session-table/session-table.component';
import { AnyoSortDirection } from 'projects/app-core/src/models/gridRequest';
import { IAnyoTherapist } from '../../../../../admin/src/app/models/IAnyoTherapist';
import Acknowledgement from '../../../models/acknowledgement';
import { SessionFeedbackFormSubmitRequest } from '../models/sessionFeedbackFormSubmitRequest';
import {
  SessionGridRequest,
  SessionGridTab,
} from '../models/sessionGridRequest';
import { SessionGridResponse } from '../models/sessionGridResponse';
import {
  CancelSessionRequest,
  ConfirmSessionRequest,
  UpdateSessionRequest,
} from '../models/SessionRequest';
import {
  SessionGridStatsData,
  SessionGridStatsResp,
} from '../models/sessionStatsData';
import { AppointmentStatus } from '../models/TherapistAppointment';

export interface SessionAddRemarkRequest {
  sessionId: string;
  remarks: string;
}

export interface SessionOverrideSideEffectsRequest {
  meetCancel?: boolean;
  notifDelete?: boolean;
}

export interface SessionOverrideRequest {
  targetStatus: string;
  reason: string;
  sideEffects?: SessionOverrideSideEffectsRequest;
}

export interface SendSessionFeedbackDocumentRequest {
  sessionIds: string[];
  therapistId: string;
}

@Injectable({
  providedIn: 'root',
})
export class SessionsService {
  constructor(
    protected httpClient: HttpClient,
    protected api: NetworkUtilsService,
  ) {}

  public syncMeetParticipantData(sessionId: string) {
    return this.api.post(URL_DICT.sessionSyncMeetParticipantDataUrl, {
      sessionId,
    });
  }

  public addRemarkForPastSession(request: SessionAddRemarkRequest) {
    return this.api.post(URL_DICT.sessionExpertAddRemark, request);
  }

  public assignGoogleMeet(sessionId: string) {
    return this.api.post<{ callLink: string }>(
      `${URL_DICT.adminSessionBase}${sessionId}/assign-google-meet`,
      {},
    );
  }

  public fetchSessions(gridRequest: SessionGridRequest) {
    return this.api.post<SessionGridResponse>(
      URL_DICT.adminSessionGrid,
      gridRequest,
    );
  }
  public confirmAppointment(
    sessionId: string,
    confirmSessionRequest: ConfirmSessionRequest,
  ) {
    return this.api.post<Acknowledgement>(
      URL_DICT.adminSessionConfirmUrl + `/${String(sessionId)}`,
      confirmSessionRequest,
    );
  }
  public rescheduleDeclineAppointment(
    sessionId: string,
    confirmSessionRequest: ConfirmSessionRequest,
  ) {
    return this.api.post<Acknowledgement>(
      URL_DICT.adminSessionRescheduledDeclineUrl + `/${String(sessionId)}`,
      confirmSessionRequest,
    );
  }

  public checkSlotsAvailable(therapistId: string) {
    return this.api.get<boolean>(
      URL_DICT.adminCheckSlotsAvailableUrl + `${therapistId}`,
    );
  }
  public canMakeTherapistVisible(therapistId: string) {
    return this.api.get<{ canMakeVisible: boolean }>(
      URL_DICT.adminCanMakeTherapistVisibleUrl + `/${therapistId}`,
    );
  }
  public cancelAppointment(
    sessionId: string,
    cancelSessionRequest: CancelSessionRequest,
  ) {
    return this.api.post<Acknowledgement>(
      URL_DICT.adminSessionCancelUrl + `/${String(sessionId)}`,
      cancelSessionRequest,
    );
  }

  public updateAppointment(
    sessionId: string,
    updateSessionRequest: UpdateSessionRequest,
  ) {
    return this.api.post(
      URL_DICT.adminSessionUpdateUrl + `/${String(sessionId)}`,
      updateSessionRequest,
    );
  }

  public updateSessionFeedbackFom(request: SessionFeedbackFormSubmitRequest) {
    return this.api.post<Acknowledgement>(
      URL_DICT.adminSessionFeedBack,
      request,
    );
  }

  public getActiveTherapists() {
    return this.api.get<IAnyoTherapist[]>(URL_DICT.getActiveTherapists);
  }

  public adminSessionBooking(request: AdminTherapistBookingRequest) {
    return this.api.post<Acknowledgement>(
      URL_DICT.adminSessionBooking,
      request,
    );
  }

  public adminSessionPacksBooking(request: AdminTherapistBookingRequest) {
    return this.api.post<Acknowledgement>(
      URL_DICT.adminSessionPacksBooking,
      request,
    );
  }

  public adminSearchUsers(searchText: string) {
    return this.api.get<SearchUserResponse[]>(
      `${URL_DICT.adminSearchUsers}?user=${searchText}`,
    );
  }

  public getAvailableSlotsTherapist(selectedTherapist: string, label: string) {
    return this.api.get<Record<string, SlotAvailability[]>>(
      URL_DICT.getAvailableSlots + `${selectedTherapist}?email=${label}`,
    );
  }

  public fetchSessionsForTherapist(gridRequest: SessionGridRequest) {
    return this.api.post<SessionGridResponse>(
      URL_DICT.supportPortal + 'sessions/grid',
      gridRequest,
    );
  }

  public sessionGridStats(request: SessionGridRequest) {
    return this.api.post<SessionGridStatsResp>(
      URL_DICT.sessionStatsUrl,
      request,
    );
  }

  public mapSessionGridStatsResp(value: SessionGridStatsResp) {
    const response: SessionGridStatsData = {
      totalSessions: value.totalSessions,
      confirmedSessions:
        value.allOtherSessions.find(
          (data) => data._id === AppointmentStatus.CONFIRMED,
        )?.count || 0,
      cancelledSessions:
        value.allOtherSessions.find(
          (data) => data._id === AppointmentStatus.CANCELLED,
        )?.count || 0,
      completedSessions:
        value.allOtherSessions.find(
          (data) => data._id === AppointmentStatus.COMPLETED,
        )?.count || 0,
      noShowUserSessions:
        value.allOtherSessions.find(
          (data) => data._id === AppointmentStatus.NO_SHOW_USER,
        )?.count || 0,
      noShowTherapistSessions:
        value.allOtherSessions.find(
          (data) => data._id === AppointmentStatus.NO_SHOW_THERAPIST,
        )?.count || 0,
      techIssueSessions:
        value.allOtherSessions.find(
          (data) => data._id === AppointmentStatus.TECH_ISSUE,
        )?.count || 0,
      feedbackPendingSessions:
        value.allOtherSessions.find(
          (data) => data._id === AppointmentStatus.FEEDBACK_PENDING,
        )?.count || 0,
    };
    return response;
  }

  // Base64 encoding/decoding methods for URL state management
  public encodeGridRequestToBase64(gridRequest: SessionGridRequest): string {
    try {
      const jsonString = JSON.stringify(gridRequest);
      return btoa(jsonString);
    } catch (error) {
      console.error('Error encoding grid request:', error);
      return '';
    }
  }

  public decodeGridRequestFromBase64(
    encodedRequest: string,
  ): SessionGridRequest | null {
    try {
      const jsonString = atob(encodedRequest);
      return JSON.parse(jsonString) as SessionGridRequest;
    } catch (error) {
      console.error('Error decoding grid request:', error);
      return null;
    }
  }

  public overrideSessionStatus(
    sessionId: string,
    request: SessionOverrideRequest,
  ) {
    return this.api.post<Acknowledgement>(
      URL_DICT.adminSessionOverrideStatusUrl + `${sessionId}/override-status`,
      request,
    );
  }

  public buildSessionGridRequest(): SessionGridRequest {
    return {
      sort: {
        SESSION_DATE: AnyoSortDirection.DESC,
        THERAPIST_RATING: AnyoSortDirection.DESC,
      },
      dateFilters: undefined,
      export: false,
      filters: {
        SESSION_STATUS: [],
        THERAPIST_EMAIL: [],
        USER_ID: [],
        PARTNER_ID: [],
        HIDE_TEST_BOOKING: ['true'],
        SESSION_TAG: [],
      },
      from: undefined,
      globalSearch: [],
      page: 1,
      pageSize: 10,
      searchQuery: undefined,
      tab: SessionGridTab.NEW_APPOINTMENTS,
      to: undefined,
    };
  }

  public sendFeedbackDocument(req: SendSessionFeedbackDocumentRequest) {
    return this.api.post(
      URL_DICT.adminSendSessionFeedbackDocumentUrl,
      req,
    );
  }
}
